import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import DashboardChatbotRuntimeCard from '@/components/dashboard/DashboardChatbotRuntimeCard';
import { DashboardInsightsSection } from '@/components/dashboard/DashboardPanels';
import { useDashboardSite } from '@/pages/dashboard/DashboardSiteContext';

export default function DashboardOverviewPage() {
  const {
    selectedSite,
    selectedSiteId,
    billing,
    calendarConnection,
    emailConnection,
    embedInfo,
    bookings,
    formatBookingWhen,
    refreshBilling,
  } = useDashboardSite();

  const steps: { label: string; to: string; reason: string }[] = [];
  if (billing?.requires_prepaid_balance && (billing.payment_required || Number(billing.balance_usd || 0) <= 0)) {
    steps.push({ label: 'Add funds', to: '/dashboard/billing', reason: 'Chat is gated until prepaid balance is positive.' });
  }
  if (billing?.requires_prepaid_balance && !billing.has_payment_method) {
    steps.push({ label: 'Save a payment method', to: '/dashboard/billing', reason: 'Needed for auto-recharge.' });
  }
  if (Number(selectedSite?.page_count ?? 0) === 0 && selectedSite) {
    steps.push({
      label: 'Crawl your site',
      to: '/dashboard/content',
      reason: 'Your assistant has no content yet.',
    });
  }
  if (!selectedSite) {
    steps.push({
      label: 'Claim your website',
      to: '/dashboard/content',
      reason: 'Add your one website and start the first crawl.',
    });
  }
  if (!calendarConnection?.connected) {
    steps.push({
      label: 'Connect calendar',
      to: '/dashboard/schedule',
      reason: 'Required before Allow booking inside chat. Bookings then sync to Google or Calendly.',
    });
  } else if (calendarConnection.needs_setup) {
    steps.push({
      label: 'Choose calendar target',
      to: '/dashboard/schedule',
      reason: 'Pick the Google calendar or Calendly event type, then enable in-chat booking on Automation.',
    });
  }
  if (!emailConnection?.connected) {
    steps.push({
      label: 'Connect Gmail for booking emails',
      to: '/dashboard/automation',
      reason: 'Visitor confirmations send from your Gmail. Separate from calendar — disconnecting Gmail does not turn off booking.',
    });
  }
  if (embedInfo && steps.length < 3) {
    steps.push({ label: 'Copy embed snippet', to: '/dashboard/widget', reason: 'Add the chat widget to your site.' });
  }
  const nextSteps = steps.slice(0, 3);
  const recent = (bookings || []).slice(0, 5);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">{selectedSite?.name || 'Overview'}</h2>
        <p className="text-sm text-muted-foreground">
          Status, usage, and what needs attention.{' '}
          <Link to="/dashboard/help" className="underline underline-offset-2">
            How to use the dashboard
          </Link>
        </p>
        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {billing ? (
            <span className="rounded-md border border-border px-2 py-1 text-muted-foreground">
              Balance ${Number(billing.balance_usd || 0).toFixed(2)}
              {billing.payment_required ? ' · chat gated' : ''}
              {billing.chatbot_running === false ? ' · chatbot stopped' : ''}
              {billing.chatbot_running !== false && billing.hourly_runtime_billing_active
                ? ` · ${Number(billing.running_hours_total ?? 0).toFixed(1)}h runtime`
                : ''}
            </span>
          ) : null}
          <span className="rounded-md border border-border px-2 py-1 text-muted-foreground">
            Calendar {calendarConnection?.connected ? `connected (${calendarConnection.provider})` : 'not connected'}
          </span>
          <span className="rounded-md border border-border px-2 py-1 text-muted-foreground">
            Booking email{' '}
            {emailConnection?.connected
              ? `Gmail (${emailConnection.connected_account || 'connected'})`
              : 'not connected'}
          </span>
        </div>
      </div>

      {nextSteps.length > 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>Next steps</CardTitle>
            <CardDescription>
              Finish these to get the most from your assistant.{' '}
              <Link to="/dashboard/help#get-started" className="underline underline-offset-2">
                Full setup guide
              </Link>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {nextSteps.map((step) => (
              <div key={step.to + step.label} className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-medium">{step.label}</div>
                  <div className="text-xs text-muted-foreground">{step.reason}</div>
                </div>
                <Button asChild size="sm" variant="outline">
                  <Link to={step.to}>{step.label}</Link>
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      ) : null}

      <DashboardInsightsSection />

      {selectedSiteId ? (
        <DashboardChatbotRuntimeCard
          siteId={selectedSiteId}
          billing={billing}
          onRefresh={refreshBilling}
        />
      ) : null}

      {billing ? (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <div>
              <CardTitle>Prepaid balance</CardTitle>
              <CardDescription>
                ${Number(billing.balance_usd || 0).toFixed(2)}
                {billing.stripe_test_mode ? ' · test mode' : ''}
              </CardDescription>
            </div>
            <Button asChild size="sm" variant="outline">
              <Link to="/dashboard/billing">Manage billing</Link>
            </Button>
          </CardHeader>
        </Card>
      ) : null}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <CardTitle>Recent bookings</CardTitle>
            <CardDescription>Latest activity for this site.</CardDescription>
          </div>
          <Button asChild size="sm" variant="outline">
            <Link to="/dashboard/bookings">View all</Link>
          </Button>
        </CardHeader>
        <CardContent>
          {recent.length === 0 ? (
            <p className="text-sm text-muted-foreground">No bookings yet.</p>
          ) : (
            <ul className="divide-y divide-border text-sm">
              {recent.map((b) => (
                <li key={b.id} className="flex flex-wrap justify-between gap-2 py-2">
                  <span>{b.lead_name || b.lead_email || `Booking #${b.id}`}</span>
                  <span className="text-muted-foreground">{formatBookingWhen(b)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
